import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { Dispatch } from "redux"
import { Box, Typography, TextField, Button, Link, CircularProgress,
  FormControlLabel, Checkbox, FormControl, IconButton, InputAdornment,
  InputLabel, OutlinedInput, FormHelperText
} from "@mui/material"
import { VisibilityOff, Visibility } from "@mui/icons-material"

import { setUser } from "../redux/actionCreators"
import { post } from "../utils/http"
import { clearLoginCache } from '../utils/authVerify'
import { isPrivate } from "../utils/config"
import logo from '../assets/pathomiq-logo.png'

interface FormValues {
  [key: string]: string,
  email: string,
  password: string
}

const Login = () => {
  const [loginForm, setLoginForm] = useState<FormValues>({email: localStorage.getItem('user_email') || '', password: ''})
  const [loginFormError, setLoginFormError] = useState<FormValues>({email: '', password: ''})
  const [formResMsg, setFormResMsg] = useState<string>('')
  const [rememberMe, setRememberMe] = useState<boolean>(localStorage.getItem('rememberMe') === 'true')

  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [isLoading, setIsloading] = useState<boolean>(false)
  
  const minPassLength: number = 8

  const navigate = useNavigate()
  const dispatch: Dispatch<any> = useDispatch()

  const saveUser = React.useCallback((user: IUser) => dispatch(setUser(user)), [dispatch])

  useEffect(() => {
    clearLoginCache()
  }, [])

  const loginHandler = async () => {
    setIsloading(true)
    try {
      if (!validateForm()) throw Error('Invalid form data')
      const res = await post("Users/login", loginForm)
      localStorage.setItem("user_id", res.id)
      localStorage.setItem("user_email", res.email)
      localStorage.setItem("role", res.role)
      localStorage.setItem("fullName", `${res.title} ${res.first_name} ${res.last_name}`)
      localStorage.setItem("entity_id", res.entityid)
      if (/admin|superadmin|dev/i.test(res.role)) localStorage.setItem("adminId", res.id)
      saveUser({id: 1, name: res.first_name, token: res.access_token})
      setIsloading(false)
      
      if (/admin|superadmin|dev/i.test(res.role) || isPrivate) navigate("/dashboard", {replace: true})
      else navigate("/home", {replace: true})
    } catch (err) {
      console.log(err)
      setFormResMsg('Invalid login credentials, Please try again')
      setIsloading(false)
    }
  }

  const validateForm = () => {
    let valid = true

    // email check
    if (!loginForm.email) {
      setLoginFormError(prevForm => ({...prevForm, email: 'Email is required'}))
      valid = false
    } else {
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i
      if (!emailRegex.test(loginForm.email)) {
        setLoginFormError(prevForm => ({...prevForm, email: 'Invalid email address'}))
        valid = false
      } else setLoginFormError(prevForm => ({...prevForm, email: ''}))
    }
    // password check
    if (!loginForm.password) {
      setLoginFormError(prevForm => ({...prevForm, password: 'Password is required'}))
      valid = false
    } else if (loginForm.password.length < minPassLength) {
      setLoginFormError(prevForm => ({...prevForm, password: 'Your password must be at least 8 characters'}))
      valid = false
    } else setLoginFormError(prevForm => ({...prevForm, password: ''}))

    return valid
  }

  const handleKeyChange = (e: any) => {
    if (e.key === 'Enter') loginHandler()
  }

  const handleChange = (e: any) => {
    setFormResMsg('')
    setLoginForm(prevForm => ({...prevForm, [e.target.name]: e.target.value}))
    setLoginFormError(prevForm => ({...prevForm, [e.target.name]: ''}))
  }

  useEffect(() => {
    if (rememberMe) localStorage.setItem('rememberMe', 'true')
    else localStorage.removeItem('rememberMe')
  }, [rememberMe])

  const handleRemberMe = (e: any) => setRememberMe(e.target.checked)

  const handleClickShowPassword = () => setShowPassword(show => !show)
  const handleMouseDownPassword = (e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()
  const handleMouseUpPassword = (e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()

  return (
    <Box className='d-flex justify-content-center align-items-center' sx={{height: '100vh'}}>
      {isPrivate &&
        <Typography className='position-absolute m-0' sx={{top: '1rem', left: '1rem'}} variant='caption'>
          Internal
        </Typography>
      }
      <Box className='d-flex flex-column align-items-center gap-3 p-5 w-100' component="form" sx={{minWidth: 300, maxWidth: 450}}>
        <img src={logo} width={80} className='mb-4' alt="PATHOMIQ"/>
        <TextField
          label="Email"
          name='email'
          autoComplete="username"
          variant="outlined"
          value={loginForm.email}
          onChange={handleChange}
          error={!!loginFormError.email}
          helperText={loginFormError.email}
          sx={{width: '100%'}}
          onKeyUp={handleKeyChange}
          required
        />
        <FormControl sx={{width: '100%'}} variant="outlined" error={!!loginFormError.password} required>
          <InputLabel htmlFor="login-password">Password</InputLabel>
          <OutlinedInput id="login-password" type={showPassword ? 'text' : 'password'} 
            endAdornment={
              <InputAdornment position="end">
                <IconButton aria-label={showPassword ? 'hide the password' : 'display the password'}
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  onMouseUp={handleMouseUpPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            label="Password"
            name='password'
            autoComplete="current-password"
            value={loginForm.password}
            onChange={handleChange}
            onKeyUp={handleKeyChange}
          />
          <FormHelperText error={true}>{loginFormError.password}</FormHelperText>
        </FormControl>
        <Box className='w-100 d-flex justify-content-between align-items-center flex-wrap'>
          <FormControlLabel control={<Checkbox onChange={handleRemberMe} checked={rememberMe}/>} label="Remember me" />
          <Link href="../forgot">Forgot password?</Link>
        </Box>
        {formResMsg.length ? <Typography color='error'>{formResMsg}</Typography> : null}
        <Button className='p-2 w-100 fs-6' variant="contained" onClick={loginHandler} disabled={isLoading}>
          {isLoading ? <CircularProgress size={25}/> : 'Login'}
        </Button>
        <Box className='d-flex justify-content-center'>
          <Typography>Not registered? <Link href="../signup">Sign up</Link></Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default Login
